import { useCallback, useEffect, useState } from "react";
import { showToast } from "../../../libraries/clipboardUtils";
import {
  getActZoneTerminalURL,
  getActZoneTerminalsURL,
  getActZoneURL,
  getActZonesURL
} from "../../../libraries/apiUrl";
import { _delete, _get, _patch, _post } from "../../../libraries/fetch";

const emptyZoneForm = { ZoneName: "", IsSelect: 1 };
const emptyTerminalForm = { TerminalName: "", deviceAccessKey: "" };

function getErrorMessage(result, fallback) {
  if (!result) return fallback;
  if (typeof result.message === "string") return result.message;
  if (Array.isArray(result.message)) return result.message.join(", ");
  return fallback;
}

function unwrapZones(data) {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.result)) return data.result;
  return null;
}

function ZoneManagement({ branchId }) {
  const [zones, setZones] = useState([]);
  const [loading, setLoading] = useState(false);
  const [zoneForm, setZoneForm] = useState(emptyZoneForm);
  const [editingZoneId, setEditingZoneId] = useState("");
  const [isZonePopupOpen, setIsZonePopupOpen] = useState(false);
  const [terminalForm, setTerminalForm] = useState(emptyTerminalForm);
  const [terminalZoneId, setTerminalZoneId] = useState("");
  const [editingTerminalId, setEditingTerminalId] = useState("");
  const [isTerminalPopupOpen, setIsTerminalPopupOpen] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState(null);
  const [saving, setSaving] = useState(false);

  const loadZones = useCallback(async () => {
    if (!branchId) return;
    setLoading(true);
    try {
      const data = await _get(getActZonesURL(branchId), { useCache: false });
      const nextZones = unwrapZones(data);
      if (nextZones === null) {
        showToast(getErrorMessage(data, "Failed to load ACT zones"));
        setZones([]);
        return;
      }
      setZones(nextZones);
    } catch (error) {
      showToast(error.message || "Failed to load ACT zones");
      setZones([]);
    } finally {
      setLoading(false);
    }
  }, [branchId]);

  useEffect(() => {
    loadZones();
  }, [loadZones]);

  const selectedTerminalZone = zones.find((zone) => zone._id === terminalZoneId);

  const resetZoneForm = () => {
    setZoneForm(emptyZoneForm);
    setEditingZoneId("");
  };

  const resetTerminalForm = () => {
    setTerminalForm(emptyTerminalForm);
    setTerminalZoneId("");
    setEditingTerminalId("");
  };

  const closeZonePopup = () => {
    if (saving) return;
    setIsZonePopupOpen(false);
    resetZoneForm();
  };

  const closeTerminalPopup = () => {
    if (saving) return;
    setIsTerminalPopupOpen(false);
    resetTerminalForm();
  };

  const openCreateArea = () => {
    resetZoneForm();
    setIsZonePopupOpen(true);
  };

  const openEditArea = (zone) => {
    setEditingZoneId(zone._id);
    setZoneForm({
      ZoneName: zone.ZoneName || "",
      IsSelect: Number(zone.IsSelect) === 1 ? 1 : 0
    });
    setIsZonePopupOpen(true);
  };

  const openAddTerminal = (zone) => {
    resetTerminalForm();
    setTerminalZoneId(zone._id);
    setIsTerminalPopupOpen(true);
  };

  const openEditTerminal = (zone, terminal) => {
    setTerminalZoneId(zone._id);
    setEditingTerminalId(terminal._id);
    setTerminalForm({
      TerminalName: terminal.TerminalName || "",
      deviceAccessKey: terminal.deviceAccessKey || ""
    });
    setIsTerminalPopupOpen(true);
  };

  const saveZone = async (event) => {
    event.preventDefault();
    if (!zoneForm.ZoneName.trim()) {
      showToast("Area name is required");
      return;
    }
    setSaving(true);
    try {
      if (editingZoneId) {
        const result = await _patch(getActZoneURL(branchId, editingZoneId), {
          ZoneName: zoneForm.ZoneName.trim(),
          IsSelect: Number(zoneForm.IsSelect)
        });
        if (result?.success === false) {
          throw new Error(getErrorMessage(result, "Failed to update area"));
        }
        showToast("Area updated");
      } else {
        const result = await _post(getActZonesURL(branchId), {
          ZoneName: zoneForm.ZoneName.trim(),
          IsSelect: Number(zoneForm.IsSelect),
          Terminals: []
        });
        if (result?.success === false) {
          throw new Error(getErrorMessage(result, "Failed to create area"));
        }
        showToast("Area created");
      }
      setIsZonePopupOpen(false);
      resetZoneForm();
      await loadZones();
    } catch (error) {
      showToast(error.message || "Failed to save area");
    } finally {
      setSaving(false);
    }
  };

  const saveTerminal = async (event) => {
    event.preventDefault();
    if (!terminalZoneId) {
      showToast("Select an area for this terminal");
      return;
    }
    if (!terminalForm.TerminalName.trim() || !terminalForm.deviceAccessKey.trim()) {
      showToast("Terminal name and device access key are required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        TerminalName: terminalForm.TerminalName.trim(),
        deviceAccessKey: terminalForm.deviceAccessKey.trim()
      };
      const result = editingTerminalId
        ? await _patch(
            getActZoneTerminalURL(branchId, terminalZoneId, editingTerminalId),
            payload
          )
        : await _post(getActZoneTerminalsURL(branchId, terminalZoneId), payload);
      if (result?.success === false) {
        throw new Error(getErrorMessage(result, "Failed to save terminal"));
      }
      showToast(editingTerminalId ? "Terminal updated" : "Terminal added");
      setIsTerminalPopupOpen(false);
      resetTerminalForm();
      await loadZones();
    } catch (error) {
      showToast(error.message || "Failed to save terminal");
    } finally {
      setSaving(false);
    }
  };

  const askRemoveZone = (zone) => {
    setConfirmDialog({
      type: "zone",
      title: "Are you sure?",
      message: `Delete area "${zone.ZoneName}" and its terminals? This cannot be undone.`,
      confirmLabel: "Yes, delete",
      zone
    });
  };

  const askRemoveTerminal = (zone, terminal) => {
    setConfirmDialog({
      type: "terminal",
      title: "Are you sure?",
      message: `Delete terminal "${terminal.TerminalName}"? This cannot be undone.`,
      confirmLabel: "Yes, delete",
      zone,
      terminal
    });
  };

  const closeConfirmDialog = () => {
    if (saving) return;
    setConfirmDialog(null);
  };

  const confirmRemove = async () => {
    if (!confirmDialog) return;
    setSaving(true);
    try {
      if (confirmDialog.type === "zone") {
        const result = await _delete(getActZoneURL(branchId, confirmDialog.zone._id));
        if (result?.success === false) {
          throw new Error(getErrorMessage(result, "Failed to delete area"));
        }
        showToast("Area deleted");
      } else {
        const result = await _delete(
          getActZoneTerminalURL(branchId, confirmDialog.zone._id, confirmDialog.terminal._id)
        );
        if (result?.success === false) {
          throw new Error(getErrorMessage(result, "Failed to delete terminal"));
        }
        showToast("Terminal deleted");
      }
      setConfirmDialog(null);
      await loadZones();
    } catch (error) {
      showToast(error.message || "Failed to delete");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ padding: "16px 20px", color: "#e5e7eb" }}>
      <div style={headerStyle}>
        <div>
          <h2 style={{ margin: 0 }}>Zone Management</h2>
          <p style={{ color: "#9ca3af", margin: "8px 0 0" }}>
            Configure ACT areas and terminal device access keys for this branch.
            Legacy ACT ids are generated automatically.
          </p>
        </div>
        <button type="button" onClick={openCreateArea} style={buttonStyle}>
          Create new area
        </button>
      </div>

      {loading ? <p>Loading areas...</p> : null}
      {!loading && zones.length === 0 ? <p>No ACT areas configured yet.</p> : null}

      {zones.map((zone) => (
        <div key={zone._id} style={cardStyle}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
            <div>
              <h3 style={{ margin: "0 0 6px" }}>{zone.ZoneName}</h3>
              <div style={{ color: "#9ca3af", fontSize: 13 }}>
                IsSelect: {Number(zone.IsSelect) === 1 ? "1" : "0"} · zone id: {zone.id}
              </div>
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "flex-end" }}>
              <button type="button" style={buttonStyle} onClick={() => openAddTerminal(zone)}>
                Add terminal
              </button>
              <button type="button" style={secondaryButtonStyle} onClick={() => openEditArea(zone)}>
                Edit
              </button>
              <button type="button" style={dangerButtonStyle} onClick={() => askRemoveZone(zone)}>
                Delete
              </button>
            </div>
          </div>
          <div className="table-scroll">
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={thStyle}>Terminal</th>
                <th style={thStyle}>Device access key</th>
                <th style={thStyle}></th>
              </tr>
            </thead>
            <tbody>
              {(zone.Terminals || []).length === 0 ? (
                <tr>
                  <td colSpan={3} style={tdStyle}>
                    No terminals
                  </td>
                </tr>
              ) : (
                (zone.Terminals || []).map((terminal) => (
                  <tr key={terminal._id}>
                    <td style={tdStyle}>{terminal.TerminalName}</td>
                    <td style={tdStyle}>
                      <code>{terminal.deviceAccessKey || "—"}</code>
                    </td>
                    <td style={tdStyle}>
                      <button
                        type="button"
                        style={secondaryButtonStyle}
                        onClick={() => openEditTerminal(zone, terminal)}
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        style={{ ...dangerButtonStyle, marginLeft: 8 }}
                        onClick={() => askRemoveTerminal(zone, terminal)}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
          </div>
        </div>
      ))}

      {isZonePopupOpen ? (
        <div
          style={overlayStyle}
          role="dialog"
          aria-modal="true"
          aria-label={editingZoneId ? "Edit area" : "Create new area"}
          onClick={closeZonePopup}
        >
          <div style={modalStyle} onClick={(event) => event.stopPropagation()}>
            <div style={modalHeaderStyle}>
              <div>
                <h3 style={modalTitleStyle}>{editingZoneId ? "Edit area" : "Create new area"}</h3>
                <p style={modalSubtitleStyle}>
                  {editingZoneId
                    ? "Update this area name and selection flag."
                    : "Add an ACT area for this branch. You can add terminals after it is created."}
                </p>
              </div>
              <button type="button" style={closeButtonStyle} onClick={closeZonePopup} aria-label="Close">
                X
              </button>
            </div>
            <form onSubmit={saveZone} style={formGridStyle}>
              <input
                placeholder="Area name"
                value={zoneForm.ZoneName}
                onChange={(event) =>
                  setZoneForm((current) => ({ ...current, ZoneName: event.target.value }))
                }
                style={inputStyle}
                autoFocus
              />
              <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <input
                  type="checkbox"
                  checked={Number(zoneForm.IsSelect) === 1}
                  onChange={(event) =>
                    setZoneForm((current) => ({
                      ...current,
                      IsSelect: event.target.checked ? 1 : 0
                    }))
                  }
                />
                Selected (IsSelect = 1)
              </label>
              <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                <button type="button" onClick={closeZonePopup} style={secondaryButtonStyle}>
                  Cancel
                </button>
                <button type="submit" disabled={saving} style={buttonStyle}>
                  {saving
                    ? "Saving..."
                    : editingZoneId
                      ? "Update area"
                      : "Create area"}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}

      {isTerminalPopupOpen ? (
        <div
          style={overlayStyle}
          role="dialog"
          aria-modal="true"
          aria-label={editingTerminalId ? "Edit terminal" : "Add terminal"}
          onClick={closeTerminalPopup}
        >
          <div style={modalStyle} onClick={(event) => event.stopPropagation()}>
            <div style={modalHeaderStyle}>
              <div>
                <h3 style={modalTitleStyle}>
                  {editingTerminalId ? "Edit terminal" : "Add terminal"}
                </h3>
                <p style={modalSubtitleStyle}>
                  {selectedTerminalZone
                    ? `${editingTerminalId ? "Update" : "Add"} a terminal for ${selectedTerminalZone.ZoneName}.`
                    : "Enter terminal details for this area."}
                </p>
              </div>
              <button type="button" style={closeButtonStyle} onClick={closeTerminalPopup} aria-label="Close">
                X
              </button>
            </div>
            <form onSubmit={saveTerminal} style={formGridStyle}>
              <input
                placeholder="Terminal name"
                value={terminalForm.TerminalName}
                onChange={(event) =>
                  setTerminalForm((current) => ({
                    ...current,
                    TerminalName: event.target.value
                  }))
                }
                style={inputStyle}
                autoFocus
              />
              <input
                placeholder="Device access key"
                value={terminalForm.deviceAccessKey}
                onChange={(event) =>
                  setTerminalForm((current) => ({
                    ...current,
                    deviceAccessKey: event.target.value
                  }))
                }
                style={inputStyle}
              />
              <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                <button type="button" onClick={closeTerminalPopup} style={secondaryButtonStyle}>
                  Cancel
                </button>
                <button type="submit" disabled={saving} style={buttonStyle}>
                  {saving
                    ? "Saving..."
                    : editingTerminalId
                      ? "Update terminal"
                      : "Add terminal"}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}

      {confirmDialog ? (
        <div
          style={{ ...overlayStyle, zIndex: 1400 }}
          role="dialog"
          aria-modal="true"
          aria-label={confirmDialog.title}
          onClick={closeConfirmDialog}
        >
          <div style={confirmCardStyle} onClick={(event) => event.stopPropagation()}>
            <h3 style={confirmTitleStyle}>{confirmDialog.title}</h3>
            <p style={confirmMessageStyle}>{confirmDialog.message}</p>
            <div style={confirmActionsStyle}>
              <button
                type="button"
                onClick={closeConfirmDialog}
                disabled={saving}
                style={secondaryButtonStyle}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmRemove}
                disabled={saving}
                style={dangerButtonStyle}
              >
                {saving ? "Deleting..." : confirmDialog.confirmLabel}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

const headerStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  gap: 16,
  marginBottom: 20,
  flexWrap: "wrap"
};

const overlayStyle = {
  position: "fixed",
  inset: 0,
  zIndex: 1300,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 18,
  backgroundColor: "rgba(15, 23, 42, 0.72)",
  backdropFilter: "blur(4px)"
};

const modalStyle = {
  width: "min(480px, 100%)",
  background: "#111827",
  border: "1px solid #374151",
  borderRadius: 16,
  padding: 20,
  color: "#e5e7eb",
  boxShadow: "0 24px 70px rgba(0, 0, 0, 0.45)"
};

const modalHeaderStyle = {
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "space-between",
  gap: 16,
  marginBottom: 16
};

const modalTitleStyle = {
  margin: "0 0 6px"
};

const modalSubtitleStyle = {
  margin: 0,
  color: "#9ca3af",
  fontSize: 13
};

const closeButtonStyle = {
  border: "none",
  background: "#374151",
  color: "#fff",
  borderRadius: 8,
  width: 36,
  height: 36,
  cursor: "pointer",
  fontWeight: 700
};

const formGridStyle = {
  display: "grid",
  gap: 10
};

const cardStyle = {
  background: "#111827",
  border: "1px solid #374151",
  borderRadius: 12,
  padding: 16,
  marginBottom: 16
};

const inputStyle = {
  padding: "10px 12px",
  borderRadius: 8,
  border: "1px solid #4b5563",
  background: "#1f2937",
  color: "#fff"
};

const buttonStyle = {
  border: "none",
  background: "#16a34a",
  color: "#fff",
  borderRadius: 8,
  padding: "10px 14px",
  fontWeight: 700,
  cursor: "pointer",
  whiteSpace: "nowrap"
};

const secondaryButtonStyle = {
  ...buttonStyle,
  background: "#374151"
};

const dangerButtonStyle = {
  ...buttonStyle,
  background: "#b91c1c"
};

const tableStyle = {
  width: "100%",
  borderCollapse: "collapse",
  marginTop: 12
};

const thStyle = {
  textAlign: "left",
  borderBottom: "1px solid #374151",
  padding: "8px 6px",
  color: "#9ca3af"
};

const tdStyle = {
  borderBottom: "1px solid #1f2937",
  padding: "8px 6px"
};

const confirmCardStyle = {
  ...modalStyle,
  width: "min(400px, 100%)",
  textAlign: "center"
};

const confirmTitleStyle = {
  margin: "0 0 10px",
  fontSize: 22
};

const confirmMessageStyle = {
  margin: "0 0 20px",
  color: "#9ca3af",
  fontSize: 14,
  lineHeight: 1.5
};

const confirmActionsStyle = {
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: 10
};

export default ZoneManagement;
